import { Box, Stack, styled } from '@mui/material';
import { Typography, colors, Button, Tooltip } from '@sweep-io/sweep-design';
import AgentChatScrollArea from './AgentChatScrollArea';
import AgentInput from './agent-input/AgentInput';
import { NoteAlignment } from './ChatNote';
import { ReactNode, useCallback, useState } from 'react';
import useAgentsChats from './useAgentsChats';
import useAgentsHome from './useAgentsHome';
import { AgentNameButton } from './AgentNameButton';
import { CenteredCircularProgress } from '../common/CenteredCircularProgress';
import { useAiAgentMessages } from './useAiAgentMessages';
import { AgentType } from '@server/ai';
import { isTempChatId } from './agentsChatsReducer';
import { AgentInputMode } from './agent-input/agentInputModeTypes';
import { ShareChatDialog } from './ShareChatDialog';
import { useSelector } from 'react-redux';
import { selectDefaultCreationCrmOrgId } from '../../reducers/userInfoReducer';
import { ConversationCopyMessage } from './ConversationCopyMessage';
import useSendBiEvent from '../../hooks/useSendBiEvent';
import { AI_AGENTS_EVENTS } from '../../services/events';
import { useRunOnceWhenTruthy } from '../common/useRunOnceWhenTruthy';
import { useFeatureToggle } from '../../feature-flags/useFeatureToggle';
import {
  createMockArtifactMessage,
  createMockUserMessage,
  createMockLicenseArtifactMessage,
  createMockAgentforceArtifactMessage,
  createMockIntegrationArtifactMessage,
  createMockTechDebtArtifactMessage,
} from '../common/artifacts/mockArtifactData';
import { useDispatch } from 'react-redux';
import { addSyncMessageToActiveChat, addAsyncResponse } from './agentsChatsReducer';
import { useAgentChatInstance } from './AgentChatInstanceContext';
import { useArtifacts } from '../common/artifacts/useArtifacts';
import { useArtifactStreaming } from './useArtifactStreaming';

export type AgentChatProps = {
  noteAlignment: NoteAlignment;
  maxWidth?: string;
  searchTermToHighlight?: string; // The highlight should only be applied "expanded", so we get it as a prop
  hideFirstMessage?: boolean;
  hideOverlayTop?: boolean;
  hideAgentNameButton?: boolean;
  scrollAreaPadding?: number;
  hideAgentInfo?: boolean;
  panelButtons?: ReactNode | null;
};

export const AgentChat = ({
  noteAlignment,
  maxWidth,
  searchTermToHighlight,
  hideFirstMessage,
  hideOverlayTop,
  hideAgentNameButton,
  scrollAreaPadding,
  hideAgentInfo,
  panelButtons,
}: AgentChatProps) => {
  const { isExpanded } = useAgentsHome();
  const [selectedText, setSelectedText] = useState<string | null>(null);
  const [isStoppingResponse, setIsStoppingResponse] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const { onConfirmMessage, activeChat, activeAgent, stopResponse } = useAgentsChats();
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const sendBiEvent = useSendBiEvent();

  const { isArtifactPanelOpen } = useArtifacts();

  const { renderInitialFetchMessage } = useAiAgentMessages({
    messages: activeChat?.data?.aiChatDetails.messages ?? [],
    agentType: activeAgent?.type ?? AgentType.Documentation,
  });
  const { artifacts, dreamforceDemo } = useFeatureToggle();
  const dispatch = useDispatch();
  const { chatInstanceId } = useAgentChatInstance();

  // Handle artifact streaming (only called once per chat instance to prevent duplicates)
  useArtifactStreaming();

  const isTemp = activeChat?.data?.id ? isTempChatId(activeChat.data.id) : false;
  const readOnly = activeChat?.data?.isReadOnly ?? false;


  useRunOnceWhenTruthy(
    () => {
      sendBiEvent({
        name: AI_AGENTS_EVENTS.viewSharedChat,
        props: {
          agentId: activeAgent?.id,
          chatId: activeChat?.data?.id,
        },
      });
    },
    readOnly && !!activeChat?.data?.id && !!activeAgent?.id,
  );

  const onConfirm = useCallback(
    async (message: string, options?: { advancedMode?: AgentInputMode; additionalOrgIds?: string[] }) => {
      if (!activeAgent) {
        return;
      }
      if (artifacts && dreamforceDemo) {
        // 🎯 DREAMFORCE DEMO ONLY - Mock/demo artifact commands (requires both feature flags)
        if (message.trim() === '!artifact') {
          // Add user message first
          const userMessage = createMockUserMessage();
          dispatch(
            addSyncMessageToActiveChat({
              message: userMessage,
              agentId: activeAgent.id,
              agentType: activeAgent.type,
              chatInstanceId,
            }),
          );

          // Add artifact message after a short delay
          setTimeout(() => {
            const artifactMessage = createMockArtifactMessage();
            dispatch(
              addAsyncResponse({
                message: artifactMessage,
                agentId: activeAgent.id,
                chatInstanceId,
              }),
            );
          }, 500);

          return;
        }

        // Check for license artifact command (case insensitive)
        if (message.toLowerCase().includes('license artifact')) {
          const userMessage = {
            ...createMockUserMessage(),
            content: message,
          };
          dispatch(
            addSyncMessageToActiveChat({
              message: userMessage,
              agentId: activeAgent.id,
              agentType: activeAgent.type,
              chatInstanceId,
            }),
          );

          // Add license artifact message after a short delay
          setTimeout(() => {
            const licenseArtifactMessage = createMockLicenseArtifactMessage();
            dispatch(
              addAsyncResponse({
                message: licenseArtifactMessage,
                agentId: activeAgent.id,
                chatInstanceId,
              }),
            );
          }, 500);

          return;
        }

        // Check for agentforce artifact command (case insensitive)
        if (message.toLowerCase().includes('agentforce artifact')) {
          // Add user message first
          const userMessage = {
            ...createMockUserMessage(),
            content: message,
          };
          dispatch(
            addSyncMessageToActiveChat({
              message: userMessage,
              agentId: activeAgent.id,
              agentType: activeAgent.type,
              chatInstanceId,
            }),
          );

          // Add agentforce artifact message after a short delay
          setTimeout(() => {
            const agentforceArtifactMessage = createMockAgentforceArtifactMessage();
            dispatch(
              addAsyncResponse({
                message: agentforceArtifactMessage,
                agentId: activeAgent.id,
                chatInstanceId,
              }),
            );
          }, 500);

          return;
        }

        // Check for integration artifact command (case insensitive)
        if (message.toLowerCase().includes('integration artifact')) {
          const userMessage = {
            ...createMockUserMessage(),
            content: message,
          };
          dispatch(
            addSyncMessageToActiveChat({
              message: userMessage,
              agentId: activeAgent.id,
              agentType: activeAgent.type,
              chatInstanceId,
            }),
          );

          // Add integration artifact message after a short delay
          setTimeout(() => {
            const integrationArtifactMessage = createMockIntegrationArtifactMessage();
            dispatch(
              addAsyncResponse({
                message: integrationArtifactMessage,
                agentId: activeAgent.id,
                chatInstanceId,
              }),
            );
          }, 500);

          return;
        }

        // Check for techdebt artifact command (case insensitive)
        if (message.toLowerCase().includes('techdebt artifact')) {
          const userMessage = {
            ...createMockUserMessage(),
            content: message,
          };
          dispatch(
            addSyncMessageToActiveChat({
              message: userMessage,
              agentId: activeAgent.id,
              agentType: activeAgent.type,
              chatInstanceId,
            }),
          );

          // Add techdebt artifact message after a short delay
          setTimeout(() => {
            const techDebtArtifactMessage = createMockTechDebtArtifactMessage();
            dispatch(
              addAsyncResponse({
                message: techDebtArtifactMessage,
                agentId: activeAgent.id,
                chatInstanceId,
              }),
            );
          }, 500);

          return;
        }
      }

      const replyTo = selectedText || undefined;
      setSelectedText(null);

      await onConfirmMessage({
        messageStr: message,
        agentId: activeAgent.id,
        agentType: activeAgent.type,
        replyTo,
        advancedMode: options?.advancedMode,
        additionalOrgIds: options?.additionalOrgIds,
      });
    },
    [
      activeAgent,
      artifacts,
      dreamforceDemo,
      selectedText,
      onConfirmMessage,
      dispatch,
      chatInstanceId,
    ],
  );
  const isLoadingResponse = !!activeChat?.data?.isLoadingResponse;
  const isStreamingResponse = !!activeChat?.data?.isStreamingResponse;

  const handleTextSelection = useCallback((text: string | null) => {
    setSelectedText(text);
  }, []);

  const handleStopClick = useCallback(async () => {
    setIsStoppingResponse(true);
    await stopResponse();
    // Add 1-second timeout to allow backend cleanup
    setTimeout(() => {
      setIsStoppingResponse(false);
    }, 1000);
  }, [stopResponse]);

  const handleShareClick = useCallback(() => {
    setIsShareDialogOpen(true);
  }, []);

  const handleShareDialogClose = useCallback(() => {
    setIsShareDialogOpen(false);
  }, []);

  if (activeChat?.isLoadingChat) {
    return <CenteredCircularProgress />;
  }

  const isRenderConversationCopyMessage = readOnly && activeChat?.data?.userId;
  const isRenderAgentNameButton = isExpanded && activeAgent && !hideAgentNameButton;
  const containerPaddingTop = isRenderAgentNameButton ? 24 : 0; //24px in order to be vertically aligned with the "All agents" side menu item

  return (
    <Stack
      sx={{
        width: '100%',
        alignItems: 'center',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <Stack
        sx={{
          width: '100%',
          alignItems: 'center',
          overflow: 'hidden',
          flex: 1,
          paddingTop: containerPaddingTop + 'px',
          gap: 1,
        }}
      >
        {(isRenderAgentNameButton || isRenderConversationCopyMessage) && (
          <Stack
            sx={{
              padding: '0 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              gap: '8px',
            }}
          >
            <Stack direction="row" justifyContent="space-between" width="100%">
              {isRenderAgentNameButton && <AgentNameButton agent={activeAgent} />}
              <Stack direction="row" gap={1}>
                {activeChat?.data?.id && !isTemp && !isArtifactPanelOpen && (
                  <Tooltip title="Share chat" placement="top">
                    <Button
                      variant="system"
                      onClick={handleShareClick}
                      size="small"
                      startIconName="Share"
                    >
                      Share
                    </Button>
                  </Tooltip>
                )}
                {panelButtons}
              </Stack>
            </Stack>
            {isRenderConversationCopyMessage && activeChat?.data?.userId && (
              <ConversationCopyMessage userId={activeChat.data.userId} />
            )}
          </Stack>
        )}
        <Stack
          sx={{
            flex: 1,
            position: 'relative',
            height: '100%',
            width: '100%',
            overflow: 'hidden',
          }}
        >
          {!hideOverlayTop && <OverlayTop />}

          {activeChat?.data && (
            <AgentChatScrollArea
              maxWidth={maxWidth}
              searchTermToHighlight={searchTermToHighlight}
              onTextSelection={handleTextSelection}
              chatData={activeChat.data}
              hideFirstMessage={hideFirstMessage}
              scrollAreaPadding={scrollAreaPadding}
            />
          )}
          <OverlayBottom />
        </Stack>
      </Stack>
      {!readOnly && (
        <Stack
          sx={{
            width: '100%',
            maxWidth,
            padding: '12px',
            paddingTop: 0,
            gap: '12px',
          }}
          className="agent-chat-input-wrapper"
        >
          <AgentInput
            onConfirm={onConfirm}
            isLoading={isStoppingResponse}
            disabled={renderInitialFetchMessage || isTemp}
            selectedText={selectedText}
            onClearSelectedText={() => setSelectedText(null)}
            renderStopButton={
              !renderInitialFetchMessage && (isLoadingResponse || isStreamingResponse)
            }
            onStopClick={handleStopClick}
          />
          {!hideAgentInfo && (
            <Typography variant="caption" color={colors.grey[700]} textAlign={noteAlignment}>
              AI agents can make mistakes. We don't use your data for model training.
            </Typography>
          )}
        </Stack>
      )}

      {activeAgent && activeChat?.data?.id && (
        <ShareChatDialog
          isOpen={isShareDialogOpen}
          onClose={handleShareDialogClose}
          agentId={activeAgent.id}
          chatId={activeChat.data.id}
          crmOrgId={crmOrgId ?? undefined}
        />
      )}
    </Stack>
  );
};

const OverlayTop = () => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '20px',
      background: 'linear-gradient(180deg, rgba(255, 255, 255, 1)  0%, rgba(255, 255, 255, 0) 99%)',
      zIndex: 4, //should be above the chat messages and the issues table header
      pointerEvents: 'none',
    }}
  />
);

const OverlayBottom = styled(Box)({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: '20px',
  background: 'linear-gradient(0deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 99%)',
  zIndex: 1,
  pointerEvents: 'none',
});
